import React, { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import cx from 'classnames';

import {
  BetManager,
  Bus,
  constants,
  get,
  globalBetslipStateUpdate,
  SettingsManager,
  useApplicationState,
  useBetslipState,
  useGlobalBetslipState,
  useSettings,
} from '@atlas/core';
import { EventLink } from '@atlas/routing';
import { BetReports, BetslipSettingsModal, Button, Icon, Label } from '@atlas/ui';

import BetOutcomeLink from 'common/BetOutcomeLink';
import BetslipPopupWrapper from 'common/Betslip/BetslipPopupWrapper';
import GlobalBetslipLayout from 'common/Betslip/GlobalBetslipLayout';
import IframeBetslipSwitchers from 'common/Betslip/IframeBetslipSwitchers';
import { useLocalBetslip } from 'common/Betslip/LocalBetslipContext';
import {
  Tab,
  TabContent,
  TabHiddenContent,
  TabNavigator,
  tabNavigatorContext,
} from 'common/TabNavigator';
import { useOddsFormat } from 'components/Pages/Menu/OddsFormatSettings/OddsFormatProvider';

import BetslipClassic from './IFrameBetslip';
import IFrameQuickBet from './IFrameQuickBet';
import PostMessageActionButton from './PostMessageActionButton';

const MyOrders = lazy(
  () => import(/* webpackChunkName: "History" */ 'common/History/MyOrders/MyOrdersBetslipPopup'),
);
const MyBets = lazy(() => import('common/History/MyBets'));

const removeAllBetItems = () => BetManager.clearBets();
const cleanUpReports = () => globalBetslipStateUpdate({ reports: [] });

const AuthenticatedView = ({ children }) => {
  const { authenticated } = useApplicationState();

  if (!authenticated) {
    return (
      <div className="notification-state-popup with-button">
        <Label className="title-text" message="You are not logged into your account" />

        <Label className="sub-title-text" message="To see your bets please sign up or sign in" />

        <div className="sticky-bottom-buttons-container double absolute">
          <PostMessageActionButton
            action="ui:signup"
            message="sign_up"
            size="big"
            type="secondary"
          />
          <PostMessageActionButton action="ui:signin" message="sign_in" size="big" type="primary" />
        </div>
      </div>
    );
  }

  return children;
};

function BetslipViewController(props) {
  const { quickBet: isQuickBet } = useSettings();
  const [, setActiveIndex] = tabNavigatorContext();

  const { betIdToEdit, isOpenPopup, setIsOpenPopup, setIsOrderBet, tabIndex } = useLocalBetslip();

  useEffect(() => {
    // switch on betslip when the first bet item was added
    Bus.on(`betslip:main:${Bus.events.bet.snapshot}`, ({ addition, betItems }) => {
      if (addition) {
        globalBetslipStateUpdate({ reports: [] });

        if (betItems && betItems.length === 1 && BetManager.target === 'main') {
          setActiveIndex(0);
          setIsOpenPopup(true);
        }
      }
      if (betItems && betItems.length === 0) {
        setIsOrderBet(false);
      }
    });
  }, [setIsOrderBet, setActiveIndex, setIsOpenPopup]);

  React.useEffect(() => {
    const betslipToggleHandler = () => {
      setActiveIndex(0);
      setIsOpenPopup(!isOpenPopup);
    };

    const betslipOpenHandler = (index = 0) => {
      if (index < 0) {
        setIsOpenPopup(false);
      } else {
        setActiveIndex(index);
        setIsOpenPopup(true);
      }
    };

    Bus.emit('ui:betslip_opened', isOpenPopup);
    Bus.on('ui:betslip_toggle', betslipToggleHandler);
    Bus.on('ui:betslip_open', betslipOpenHandler);

    return () => {
      Bus.off('ui:betslip_toggle', betslipToggleHandler);
      Bus.off('ui:betslip_open', betslipOpenHandler);
    };
  }, [isOpenPopup, setIsOpenPopup, setActiveIndex]);

  useEffect(() => {
    let betslipToFocus = 'main';
    if (betIdToEdit) {
      betslipToFocus = 'edit';
    } else if (isQuickBet) {
      betslipToFocus = 'quick';
    }
    if (BetManager.target !== betslipToFocus) {
      BetManager.focus(betslipToFocus);
    }
  }, [isQuickBet, betIdToEdit]);

  useLayoutEffect(() => {
    if (isOpenPopup && !isQuickBet) {
      const element = document.querySelector('.bet-items');

      if (element) {
        element.scrollTop = element.scrollHeight;
      }
    }
  }, [isOpenPopup, tabIndex, isQuickBet]);

  return props.children;
}

const IFrameBetslipContainer = () => {
  const { quickBet: isQuickBet } = useSettings();
  const { authenticated } = useApplicationState();
  const history = useHistory();
  const { betIdToEdit, isOrderBet, setIsOpenPopup, setShowEditBetCancelPopup } = useLocalBetslip();
  const [isVisibleBetslipSettings, setVisibleBetslipSettings] = useState(false);
  const { changeOddsFormat, oddsFormat } = useOddsFormat();

  const { betItems, settings } = useBetslipState();
  const { reports } = useGlobalBetslipState();
  const { orderBet } = settings;

  const onCloseHandler = useCallback(() => {
    if (isQuickBet) {
      SettingsManager.updateSettings({ quickBet: false });
    }

    if (reports && reports.length > 0) {
      // reset bet state if betslip was submitted
      cleanUpReports();
      removeAllBetItems();
    }
    if (betIdToEdit) {
      setShowEditBetCancelPopup(true);
      return;
    }
    setIsOpenPopup(false);
  }, [reports, setIsOpenPopup, isQuickBet, betIdToEdit, setShowEditBetCancelPopup]);

  const popupWrapperClassName = cx('betslip-popup-container partner-frame', {
    empty: betItems.length === 0,
    'has-items': betItems.length > 1,
    'iframe-betslip': get(window.SYS_CONFIG, 'footer_hidden', false),
    'quick-bet': isQuickBet,
    single: betItems.length === 1,
    'single-order-bet': isOrderBet,
    'single-place-bet': reports.length === 1 && reports[0].betType === constants.BET_TYPE.SINGLE,
  });

  const fullscreenClass = cx('fullscreen' && get(window.SYS_CONFIG, 'footer_hidden', false));

  const handleOpenBetHistory = useCallback(() => {
    history.push('/bet-history');
    setIsOpenPopup(false);
  }, []);

  const closeBetslipSettings = useCallback(() => {
    setVisibleBetslipSettings(false);
  }, [setVisibleBetslipSettings]);

  return (
    <Suspense fallback={<div />}>
      <BetslipPopupWrapper className={popupWrapperClassName} onClose={onCloseHandler}>
        <TabNavigator>
          <BetslipViewController>
            <TabHiddenContent className={fullscreenClass} index={0}>
              <IframeBetslipSwitchers
                isVisibleBetslipSettings={isVisibleBetslipSettings}
                setVisibleBetslipSettings={setVisibleBetslipSettings}
              />
              <BetReports BetOutcomeLink={BetOutcomeLink} EventLink={EventLink} isMobile />
              <GlobalBetslipLayout>
                {isQuickBet ? <IFrameQuickBet /> : <BetslipClassic />}
              </GlobalBetslipLayout>
            </TabHiddenContent>

            <TabContent className="fullscreen" index={1}>
              <AuthenticatedView>
                <Suspense fallback={<div />}>
                  <div className="my-bets-container">
                    <MyBets />
                  </div>
                </Suspense>
                <Suspense fallback={<div />}>
                  <div className="bet-checker-button bet-checker-padding">
                    <Button
                      block
                      label="bet_history"
                      onClick={handleOpenBetHistory}
                      size="big"
                      type="secondary"
                    />
                  </div>
                </Suspense>
              </AuthenticatedView>
            </TabContent>

            {orderBet && (
              <TabContent className="fullscreen" index={2}>
                <AuthenticatedView>
                  <Suspense fallback={<div />}>
                    <MyOrders BetOutcomeLink={BetOutcomeLink} />
                  </Suspense>
                </AuthenticatedView>
              </TabContent>
            )}
            {isVisibleBetslipSettings && (
              <BetslipSettingsModal
                onChangeOddsType={changeOddsFormat}
                onClose={closeBetslipSettings}
                selectedOption={oddsFormat}
              />
            )}
          </BetslipViewController>

          <div className="popup-with-tabs">
            <div className="tabs-list">
              <Tab index={0}>
                <Label message="betslip" />
              </Tab>

              {authenticated && (
                <>
                  <Tab index={1}>
                    <Label message="my_bets" />
                  </Tab>

                  {orderBet && (
                    <Tab index={2}>
                      <Label message="my_orders" />
                    </Tab>
                  )}
                </>
              )}

              <a className="tab-list-item close-betslip-container" onClick={onCloseHandler}>
                <Icon className="icon-close" height={30} name="arrow-down" width={30} />
              </a>
            </div>
          </div>
        </TabNavigator>
      </BetslipPopupWrapper>
    </Suspense>
  );
};

export default IFrameBetslipContainer;
